import React, { useEffect, useId, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import AllEventsIcon from "./icons/all-events.svg";
import CoachingSessionIcon from "./icons/coaching-session.svg";
import CommunitySupportIcon from "./icons/community-support.svg";
import DemonstrationAudienceIcon from "./icons/demonstration-audience.svg";
import GroupCoachingIcon from "./icons/group-coaching.svg";
import HostSessionIcon from "./icons/host-session.svg";
import OnDemandPracticeIcon from "./icons/on-demand-practice.svg";
import OutreachClientIcon from "./icons/outreach-client.svg";
import PaidReadingIcon from "./icons/paid-reading.svg";
import PracticeCirclesIcon from "./icons/practice-circles.svg";
import PracticeDemonstrationIcon from "./icons/practice-demonstration.svg";
import ProfessionalDemonstrationIcon from "./icons/professional-demonstration.svg";
import ScheduledPracticeIcon from "./icons/scheduled-practice.svg";
import SpeakerEventsIcon from "./icons/speaker-events.svg";
import StarterClientIcon from "./icons/starter-client.svg";
import TechnologySupportIcon from "./icons/technology-support.svg";
import "./serviceentry.css";
import { useTranslation } from "react-i18next";

const coaching30MinuteSession =
  process.env.REACT_APP_COACHING_30_Minute_Session || 64;
const coaching60MinuteSession = 92;
const practice30Url = "/booking?to=bookwithother&session_length=62&calendar=true";
const practice60Url = "/booking?to=bookwithother&session_length=26&calendar=true";
const coaching30Url = `/booking?to=coaching1on1&session_length=${coaching30MinuteSession}&calendar=true`;
const coaching60Url = `/booking?to=coaching1on1&session_length=${coaching60MinuteSession}&calendar=true`;
const setCoachBackUrl = (url) => () => localStorage.setItem("coachListBackUrl", url);
// "Support Sessions" subsession id - live: 89, staging: 86 (same name both places)
const supportSessionId = process.env.REACT_APP_SUPPORT_SESSION_ID || 86;

const serviceColumns = [
  {
    title: "Practice",
    titleKey: "practice",
    items: [
      {
        label: "1 on 1 Practices",
        labelKey: "oneOnOnePractices",
        description:
          "A 1-on-1 Mediumship practice is an exchange where two developing mediums take turns practicing evidential mediumship and providing supportive feedback to each other.",
        splitTile: true,
        optionsTitle: "Choose a session length",
        options: [
          { label: "30 Min", to: practice30Url },
          { label: "60 Min", to: practice60Url },
        ],
        icon: ScheduledPracticeIcon,
        iconAlt: "",
      },
      {
        label: "1 on 1 On Demand",
        labelKey: "oneOnOneOnDemand",
        description:
          "A 1-on-1 On Demand practice is an exchange where two developing mediums take turns practicing evidential mediumship and providing supportive feedback to each other. Matching is available during the first 15 minutes hourly, 24/7.",
        to: "/go-on-demand-practice?to=ondemand",
        icon: OnDemandPracticeIcon,
        iconAlt: "",
      },
      {
        label: "Practice Circles",
        labelKey: "practiceCircles",
        description:
          "A Group practice circle is a supportive group where developing mediums take turns practicing evidential mediumship in front of a small group to build confidence, and receive gentle feedback in a low-pressure environment. Everyone is encouraged—but not required—to take a turn.",
        to: "/go-on-demand-practice?to=allpracticecircles",
        groupEvent: true,
        icon: PracticeCirclesIcon,
        iconAlt: "",
      },
      {
        label: "Practice Demos",
        labelKey: "practiceDemos",
        description:
          "A Practice demonstration is a short gallery practice (5-7 minutes) where developing mediums demonstrate evidential mediumship, build confidence, refine skills, and receive constructive feedback from others in a group setting.",
        to: "/go-on-demand-practice?to=platformDemonstration",
        groupEvent: true,
        icon: PracticeDemonstrationIcon,
        iconAlt: "",
      },
    ],
  },
  {
    title: "Serve",
    titleKey: "serve",
    items: [
      {
        label: "Host Any",
        labelKey: "hostAny",
        description:
          "Host Any Session Type means you create and host scheduled Zoom sessions available to your VerySoul level by selecting a session type and managing participants. If it's open to your level it will be available in your drop down list under Choose Session Name when creating a schedule.",
        to: "/availability",
        icon: HostSessionIcon,
        iconAlt: "",
      },
      {
        label: "Pro Demos",
        labelKey: "proDemos",
        description:
          "A Professional demonstration is a formal group event where a VerySoul staff member introduces the event, then a working medium provides evidential communication, allowing the public audience to receive connections. Hosting available to the VerySoul Professional medium level.",
        to: "/go-on-demand-practice?to=professionalDemonstrations",
        groupEvent: true,
        icon: ProfessionalDemonstrationIcon,
        iconAlt: "",
      },
      {
        label: "Starter On Demand",
        labelKey: "starterOnDemand",
        disabled: true,
        icon: StarterClientIcon,
        iconAlt: "",
      },
      {
        label: "Outreach On Demand",
        labelKey: "outreachOnDemand",
        description:
          "An OutReach session on-demand is a free on-demand mediumship for OutReach clients, connecting them with an available working medium without scheduling ahead. Available to the VerySoul Outreach/Donation Medium level.",
        to: "/go-on-demand-client",
        icon: OutreachClientIcon,
        iconAlt: "",
      },
    ],
  },
  {
    title: "Strengthen",
    titleKey: "strengthen",
    items: [
      {
        label: "1 on 1 Coaching",
        labelKey: "oneOnOneCoaching",
        description:
          "A mediumship 1-on-1 coaching session (30 or 60 minutes) Bring mediumship questions, concerns, or experiences to receive personalized guidance and support from an experienced mentor. Coaching sessions are complimentary only when the coach has not set a coaching fee in their profile.",
        coachingTile: true,
        optionsTitle: "Choose a session length",
        options: [
          { label: "30 Min", to: coaching30Url, onClick: setCoachBackUrl(coaching30Url) },
          { label: "60 Min", to: coaching60Url, onClick: setCoachBackUrl(coaching60Url) },
        ],
        icon: CoachingSessionIcon,
        iconAlt: "",
      },
      {
        label: "Group Coaching",
        labelKey: "groupCoaching",
        description:
          "A group coaching session is a group Q&A with an experienced medium, offering guidance and shared learning around mediumship development. Not a practice environment.",
        to: "/go-on-demand-practice?to=groupcoaching",
        groupEvent: true,
        icon: GroupCoachingIcon,
        iconAlt: "",
      },
      {
        label: "Community Support Groups",
        labelKey: "communitySupportGroups",
        description:
          "A mediumship community support group is a welcoming space for mediums of all levels to connect, share experiences, ask questions, and support one another.",
        to: "/go-on-demand-practice?to=communitysupportgroups",
        groupEvent: true,
        icon: CommunitySupportIcon,
        iconAlt: "",
        smallLabel: true,
      },
      {
        label: "Paid Pro Reading",
        labelKey: "paidProReading",
        description:
          "A professional paid session Private paid session with a professional medium providing compassionate, evidential communication. Browse medium profiles and reviews to choose your medium. (Verysoul mediums are welcome to book paid sessions)",
        to: "/booking?to=professionalsessions&auto_open_professional=true&calendar=true",
        onClick: () => {
          localStorage.removeItem("bookingFrom");
        },
        icon: PaidReadingIcon,
        iconAlt: "",
      },
    ],
  },
  {
    title: "Observe",
    titleKey: "observe",
    items: [
      {
        label: "Speaker Events",
        labelKey: "speakerEvents",
        description:
          "A speaker or training event is a live or recorded educational session where VerySoul mediums or staff share training and special topics to help participants expand their knowledge and skills.",
        speakerTile: true,
        optionsTitle: "Which events would you like to see?",
        options: [
          { label: "Upcoming", to: "/go-on-demand-practice?to=speakerEvents" },
          { label: "Past", to: "/go-on-demand-practice?to=speakerEventsPast" },
        ],
        groupEvent: true,
        icon: SpeakerEventsIcon,
        iconAlt: "",
      },
      {
        label: "Practice Dem Audience",
        labelKey: "practiceDemAudience",
        description:
          "A practice demonstration (audience) is a short gallery practice (5-7 minutes) where developing mediums demonstrate evidential mediumship, build confidence, refine skills, and receive constructive feedback from others in a group setting.",
        to: "/go-on-demand-practice?to=platformDemonstration",
        groupEvent: true,
        icon: DemonstrationAudienceIcon,
        iconAlt: "",
        smallLabel: true,
      },
      {
        label: "Pro Dem Audience",
        labelKey: "proDemAudience",
        description:
          "A professional demonstration (audience) is a formal group event where a VerySoul staff member introduces the event, then a working medium provides evidential communication, allowing the public audience to receive connections.",
        to: "/go-on-demand-practice?to=professionalDemonstrations",
        groupEvent: true,
        icon: ProfessionalDemonstrationIcon,
        iconAlt: "",
      },
      {
        label: "Tech Support",
        labelKey: "techSupport",
        description:
          "A tech-support Zoom appointment is a live Zoom call where you meet directly with a support specialist for troubleshooting, setup, or real-time technical help. For email support, support@verysoul.com.",
        to: `/booking?to=supportteam&session_length=${supportSessionId}&calendar=true`,
        icon: TechnologySupportIcon,
        iconAlt: "",
      },
    ],
  },
];

function PracticeSessionTile({ item }) {
  const { t } = useTranslation("mediumMisc");
  const [showPopup, setShowPopup] = useState(false);
  const [selectedDuration, setSelectedDuration] = useState("");
  const navigate = useNavigate();

  const durationOptions = [
    { label: t("serviceEntry.durations.practice30"), path: "/booking?to=bookwithother&session_length=62&calendar=true" },
    { label: t("serviceEntry.durations.practice60"), path: "/booking?to=bookwithother&session_length=26&calendar=true" },
  ];

  const handleGo = () => {
    if (!selectedDuration) return;
    setShowPopup(false);
    setSelectedDuration("");
    localStorage.setItem("coachListBackUrl", selectedDuration);
    navigate(selectedDuration);
  };

  return (
    <>
      {showPopup && (
        <div className="split-tile-popup-overlay" onClick={() => { setShowPopup(false); setSelectedDuration(""); }}>
          <div className="split-tile-popup" onClick={(e) => e.stopPropagation()}>
            <button className="split-tile-popup-close" onClick={() => { setShowPopup(false); setSelectedDuration(""); }}>✕</button>
            <p className="split-tile-popup-title">{t("serviceEntry.chooseDuration")}</p>
            <select
              className="split-tile-popup-select"
              value={selectedDuration}
              onChange={(e) => setSelectedDuration(e.target.value)}
            >
              <option value="">{t("serviceEntry.selectLength")}</option>
              {durationOptions.map((opt) => (
                <option key={opt.path} value={opt.path}>{opt.label}</option>
              ))}
            </select>
            <button
              className="split-tile-popup-go"
              disabled={!selectedDuration}
              onClick={handleGo}
            >
              {t("serviceEntry.goToCalendar")}
            </button>
          </div>
        </div>
      )}

      <div className="service-entry-tile service-entry-tile--split">
        <div className="split-tile-header" onClick={() => setShowPopup(true)}>
          {item.icon && (
            <span className="service-entry-card-icon">
              <img src={item.icon} alt={item.iconAlt} />
            </span>
          )}
          <span className="service-entry-card-title">{item.label}</span>
        </div>
        <div className="split-tile-options">
          <Link className="split-tile-option" to="/booking?to=bookwithother&session_length=62&calendar=true">
            {t("serviceEntry.min30")}
          </Link>
          <Link className="split-tile-option" to="/booking?to=bookwithother&session_length=26&calendar=true">
            {t("serviceEntry.min60")}
          </Link>
        </div>
      </div>
    </>
  );
}

function CoachingSessionTile({ item }) {
  const { t } = useTranslation("mediumMisc");
  const [showPopup, setShowPopup] = useState(false);
  const [selectedDuration, setSelectedDuration] = useState("");
  const navigate = useNavigate();

  const durationOptions = [
    { label: t("serviceEntry.durations.coaching30"), path: `/booking?to=coaching1on1&session_length=${coaching30MinuteSession}&calendar=true` },
    { label: t("serviceEntry.durations.coaching60"), path: `/booking?to=coaching1on1&session_length=${coaching60MinuteSession}&calendar=true` },
  ];

  const handleGo = () => {
    if (!selectedDuration) return;
    setShowPopup(false);
    setSelectedDuration("");
    localStorage.setItem("coachListBackUrl", selectedDuration);
    navigate(selectedDuration);
  };

  return (
    <>
      {showPopup && (
        <div className="split-tile-popup-overlay" onClick={() => { setShowPopup(false); setSelectedDuration(""); }}>
          <div className="split-tile-popup" onClick={(e) => e.stopPropagation()}>
            <button className="split-tile-popup-close" onClick={() => { setShowPopup(false); setSelectedDuration(""); }}>✕</button>
            <p className="split-tile-popup-title">{t("serviceEntry.chooseDuration")}</p>
            <select
              className="split-tile-popup-select"
              value={selectedDuration}
              onChange={(e) => setSelectedDuration(e.target.value)}
            >
              <option value="">{t("serviceEntry.selectLength")}</option>
              {durationOptions.map((opt) => (
                <option key={opt.path} value={opt.path}>{opt.label}</option>
              ))}
            </select>
            <button
              className="split-tile-popup-go"
              disabled={!selectedDuration}
              onClick={handleGo}
            >
              {t("serviceEntry.goToCalendar")}
            </button>
          </div>
        </div>
      )}

      <div className="service-entry-tile service-entry-tile--split">
        <div className="split-tile-header" onClick={() => setShowPopup(true)}>
          {item.icon && (
            <span className="service-entry-card-icon">
              <img src={item.icon} alt={item.iconAlt} />
            </span>
          )}
          <span className="service-entry-card-title">{item.label}</span>
        </div>
        <div className="split-tile-options">
          <Link
            className="split-tile-option"
            to={`/booking?to=coaching1on1&session_length=${coaching30MinuteSession}&calendar=true`}
            onClick={() => localStorage.setItem("coachListBackUrl", `/booking?to=coaching1on1&session_length=${coaching30MinuteSession}&calendar=true`)}
          >
            {t("serviceEntry.min30")}
          </Link>
          <Link
            className="split-tile-option"
            to={`/booking?to=coaching1on1&session_length=${coaching60MinuteSession}&calendar=true`}
            onClick={() => localStorage.setItem("coachListBackUrl", `/booking?to=coaching1on1&session_length=${coaching60MinuteSession}&calendar=true`)}
          >
            {t("serviceEntry.min60")}
          </Link>
        </div>
      </div>
    </>
  );
}

function SpeakerEventsTile({ item }) {
  const { t } = useTranslation("mediumMisc");
  const [showPopup, setShowPopup] = useState(false);
  const navigate = useNavigate();

  return (
    <>
      {showPopup && (
        <div className="split-tile-popup-overlay" onClick={() => setShowPopup(false)}>
          <div className="split-tile-popup" onClick={(e) => e.stopPropagation()}>
            <button className="split-tile-popup-close" onClick={() => setShowPopup(false)}>✕</button>
            <p className="split-tile-popup-title">{t("serviceEntry.upcomingOrPast")}</p>
            <div className="split-tile-popup-actions">
              <button
                className="split-tile-popup-go"
                onClick={() => { setShowPopup(false); navigate("/go-on-demand-practice?to=speakerEvents"); }}
              >
                {t("serviceEntry.upcoming")}
              </button>
              <button
                className="split-tile-popup-go split-tile-popup-go--outline"
                onClick={() => { setShowPopup(false); navigate("/go-on-demand-practice?to=speakerEventsPast"); }}
              >
                {t("serviceEntry.past")}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="service-entry-tile service-entry-tile--split">
        {item.groupEvent && <span className="service-entry-card-tag">{t("serviceEntry.group")}</span>}
        <div className="split-tile-header" onClick={() => setShowPopup(true)}>
          {item.icon && (
            <span className="service-entry-card-icon">
              <img src={item.icon} alt={item.iconAlt} />
            </span>
          )}
          <span className="service-entry-card-title">{item.label}</span>
        </div>
        <div className="split-tile-options">
          <Link className="split-tile-option" to="/go-on-demand-practice?to=speakerEvents">
            {t("serviceEntry.upcoming")}
          </Link>
          <Link className="split-tile-option" to="/go-on-demand-practice?to=speakerEventsPast">
            {t("serviceEntry.past")}
          </Link>
        </div>
      </div>
    </>
  );
}

function ServiceTile({ item }) {
  const { t } = useTranslation("mediumMisc");
  if (item.splitTile) return <PracticeSessionTile item={item} />;
  if (item.coachingTile) return <CoachingSessionTile item={item} />;
  if (item.speakerTile) return <SpeakerEventsTile item={item} />;
  const tag = item.groupEvent ? t("serviceEntry.group") : item.tag;
  const content = (
    <>
      {tag && <span className="service-entry-card-tag">{tag}</span>}
      {item.icon && (
        <span className="service-entry-card-icon">
          <img src={item.icon} alt={item.iconAlt} />
        </span>
      )}
      <span className="service-entry-card-title">{item.label}</span>
    </>
  );

  const tileClassName = [
    "service-entry-tile",
    tag ? "service-entry-tile--tagged" : "",
    item.disabled ? "service-entry-tile--disabled" : "",
    item.smallLabel ? "service-entry-tile--small" : "",
  ]
    .filter(Boolean)
    .join(" ");

  if (item.disabled || !item.to) {
    return (
      <div className={tileClassName} aria-disabled="true">
        {content}
      </div>
    );
  }

  return (
    <Link className={tileClassName} to={item.to} onClick={item.onClick}>
      {content}
    </Link>
  );
}

function InfoButton({ label, onOpen }) {
  const { t } = useTranslation("mediumMisc");
  return (
    <button
      type="button"
      className="service-entry-info-btn"
      aria-label={t("serviceEntry.details.about", { label })}
      aria-haspopup="dialog"
      onClick={onOpen}
    >
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
        <circle cx="12" cy="12" r="9" />
        <path d="M12 11v5M12 8v.01" />
      </svg>
    </button>
  );
}

function ServiceTileWithInfo({ item, onOpenInfo }) {
  return (
    <div className="service-entry-tile-wrap">
      <ServiceTile item={item} />
      {item.description && <InfoButton label={item.label} onOpen={onOpenInfo} />}
    </div>
  );
}

// Details card opened from the (i): description + the same booking choices as the tile.
// optionsTitle / option labels stay English in the data; these map them to translation keys for display.
const OPTIONS_TITLE_KEYS = {
  "Choose a session length": "chooseLength",
  "Which events would you like to see?": "whichEvents",
};
const OPTION_LABEL_KEYS = {
  "30 Min": "min30",
  "60 Min": "min60",
  Upcoming: "upcoming",
  Past: "past",
};

function ServiceDetailsDialog({ item, columnTitle, onClose }) {
  const { t } = useTranslation("mediumMisc");
  const optionLabel = (label) =>
    OPTION_LABEL_KEYS[label]
      ? t(`serviceEntry.details.options.${OPTION_LABEL_KEYS[label]}`, { defaultValue: label })
      : label;
  const [choice, setChoice] = useState(0);
  const dialogRef = useRef(null);
  const titleId = useId();

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    dialogRef.current && dialogRef.current.focus();
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const option = item.options ? item.options[choice] : null;

  return (
    <div className="service-details-overlay" onClick={onClose}>
      <div
        className="service-details-card"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        ref={dialogRef}
        onClick={(e) => e.stopPropagation()}
      >
        <button type="button" className="service-details-close" aria-label={t("serviceEntry.details.close")} onClick={onClose}>
          ✕
        </button>
        <span className="service-details-eyebrow">{columnTitle}</span>
        <h3 className="service-details-title" id={titleId}>{item.label}</h3>
        <p className="service-details-text">{item.description}</p>

        {item.disabled ? (
          <button type="button" className="service-details-btn" disabled>
            {t("serviceEntry.details.notAvailable")}
          </button>
        ) : item.options ? (
          <>
            <span className="service-details-choice-title">
              {OPTIONS_TITLE_KEYS[item.optionsTitle]
                ? t(`serviceEntry.details.${OPTIONS_TITLE_KEYS[item.optionsTitle]}`, { defaultValue: item.optionsTitle })
                : item.optionsTitle}
            </span>
            <div className="service-details-choices">
              {item.options.map((opt, i) => (
                <button
                  type="button"
                  key={opt.label}
                  aria-pressed={choice === i}
                  onClick={() => setChoice(i)}
                >
                  {optionLabel(opt.label)}
                </button>
              ))}
            </div>
            <Link className="service-details-btn" to={option.to} onClick={option.onClick}>
              {t("serviceEntry.details.continueWith", { label: optionLabel(option.label) })}
            </Link>
          </>
        ) : (
          <Link className="service-details-btn" to={item.to} onClick={item.onClick}>
            {t("serviceEntry.details.continue")}
          </Link>
        )}
      </div>
    </div>
  );
}

export default function MediumServiceEntry() {
  const { t } = useTranslation("mediumMisc");
  const { current_mediumship_level } = useSelector((state) => state.user.user);
  const canAccessClientOnDemand =
    current_mediumship_level == 3 ||
    current_mediumship_level == 5 ||
    current_mediumship_level == 6 ||
    current_mediumship_level == 7;
  const [openInfo, setOpenInfo] = useState(null); // { item, columnTitle }
  const closeInfo = React.useCallback(() => setOpenInfo(null), []);

  return (
    <div className="service-entry-shell">
      <div className="service-entry-intro">
        <h1>{t("serviceEntry.heading")}</h1>
        {/* VerySoul Gift: buy a gift card for someone else. */}
        <Link to="/give-a-gift" state={{ from: "/service-entry" }} className="service-entry-gift-btn">
          <span className="service-entry-gift-icon" aria-hidden="true">
            <svg viewBox="0 0 24 24">
              <rect x="3" y="8" width="18" height="13" rx="2" />
              <path d="M12 8v13M3 12h18M12 8c-1.5-3-5-3.5-5-1.25S9.5 8 12 8zM12 8c1.5-3 5-3.5 5-1.25S14.5 8 12 8z" />
            </svg>
          </span>
          <span className="service-entry-gift-text">
            <span className="service-entry-gift-title">{t("serviceEntry.giftTitle")}</span>
            <span className="service-entry-gift-sub">{t("serviceEntry.giftSub")}</span>
          </span>
        </Link>
      </div>

      <div className="service-entry-board">
        <div className="service-entry-columns">
          {serviceColumns.map((column) => (
            <section className="service-entry-column" key={column.title}>
              <h2>{t(`serviceEntry.columns.${column.titleKey}`)}</h2>
              <div className="service-entry-list">
                {column.items.map((item) => {
                  const resolved =
                    item.label === "Outreach On Demand" && !canAccessClientOnDemand
                      ? { ...item, disabled: true, to: undefined }
                      : item;
                  // label/description stay English in the data (label is compared above); translate for display only
                  const display = {
                    ...resolved,
                    label: t(`serviceEntry.tiles.${item.labelKey}`),
                    description: item.description
                      ? t(`serviceEntry.descriptions.${item.labelKey}`, { defaultValue: item.description })
                      : item.description,
                  };
                  return (
                    <ServiceTileWithInfo
                      item={display}
                      key={item.label}
                      onOpenInfo={() =>
                        setOpenInfo({ item: display, columnTitle: t(`serviceEntry.columns.${column.titleKey}`) })
                      }
                    />
                  );
                })}
              </div>
            </section>
          ))}
        </div>

        <Link className="service-entry-all-events" to="/go-on-demand-practice?to=platforGroupAll">
          <span className="service-entry-card-icon service-entry-card-icon--all">
            <img src={AllEventsIcon} alt="" />
          </span>
          <span>{t("serviceEntry.allGroupEvents")}</span>
        </Link>
      </div>

      {openInfo && (
        <ServiceDetailsDialog
          item={openInfo.item}
          columnTitle={openInfo.columnTitle}
          onClose={closeInfo}
        />
      )}
    </div>
  );
}
